import AbstractController from "../../../../../vendors/services/abstractController";
import { useState } from "react";
import EventoService from "../../../../services/evento.service";
import DispositivoModel from "../../../../shared/model/dispositivo.model";
import ContribuicaoModel from "../../../../shared/model/contribuicao.model";
import ContribuicaoService from "../../../../services/contribuicao.service";
import ToastService from "../../../../../vendors/services/toastService";
import { Mensagem } from "../../../../shared/mensagem/Mensagem";
import ConfirmationUtil from "../../../../../vendors/shared/utils/ConfirmationUtil";
import FileService from "../../../../services/file.service";
import { Utils } from "../../../../shared/utils/Utils";
import FilaContribuicaoUtil from "../../../../shared/utils/FilaContribuicaoUtil";
import ContribuinteService from "../../../../services/contribuinte.service";

const LIMITE_ANEXOS = 3;

export const ETAPAS_COM_ANEXO = [
  { key: "salvando", label: "Contribuição salva" },
  { key: "anexos", label: "Anexos enviados" },
  { key: "finalizando", label: "Envio finalizado" },
];

export const ETAPAS_SEM_ANEXO = [
  { key: "salvando", label: "Contribuição salva" },
  { key: "finalizando", label: "Envio finalizado" },
];

export const ETAPAS_EDICAO_COM_ANEXO = [
  { key: "salvando", label: "Contribuição atualizada" },
  { key: "anexos", label: "Anexos atualizados" },
  { key: "finalizando", label: "Alteração finalizada" },
];

export const ETAPAS_EDICAO_SEM_ANEXO = [
  { key: "salvando", label: "Contribuição atualizada" },
  { key: "finalizando", label: "Alteração finalizada" },
];

export type SalvamentoEtapaKey = "salvando" | "anexos" | "finalizando";

interface PostSaveOptions {
  skipBlockUI?: boolean;
  skipToast?: boolean;
}

interface ExecutarFluxoSalvamentoParams {
  ocultarDialogSalvar: boolean;
  onEtapaChange: (etapa: SalvamentoEtapaKey | null) => void;
  finalizar: () => Promise<void>;
}

interface ExecutarFluxoEdicaoParams {
  onEtapaChange: (etapa: SalvamentoEtapaKey | null) => void;
}

export default class EventoContribuicaoFormController extends AbstractController {
  private service: ContribuicaoService;
  private eventoService: EventoService;
  private fileService: FileService;
  private contribuinteService: ContribuinteService;
  public dispostivo: DispositivoModel;
  public contribuicao: ContribuicaoModel;

  public evento: any;
  public setEvento: any;
  public codAudienciaPublica: any;
  public setCodAudienciaPublica: any;
  public arquivoList: any;
  public setArquivoList: any;
  public sigiloso: any;
  public setSigiloso: any;
  public protocolo: any;
  public lista: any;
  public setLista: any;
  public anexosExcluir: any;
  public setAnexosExcluir: any;

  constructor() {
    super();
    [this.codAudienciaPublica, this.setCodAudienciaPublica] = useState({});
    [this.arquivoList, this.setArquivoList] = useState([]);
    [this.sigiloso, this.setSigiloso] = useState(false);
    [this.evento, this.setEvento] = useState({});
    this.service = new ContribuicaoService();
    this.dispostivo = new DispositivoModel();
    this.contribuicao = new ContribuicaoModel();
    this.eventoService = new EventoService();
    this.fileService = new FileService();
    this.contribuinteService = new ContribuinteService();
    [this.lista, this.setLista] = useState([]);
    [this.anexosExcluir, this.setAnexosExcluir] = useState([]);
  }

  init() {
    super.init();
    const state = this.location.state;
    this.setCodAudienciaPublica(state.codAudienciaPublica);
    this.setIsView(state.isView);
    this.setIsEdit(!!state.isEdit);
    this.obterDispositivo();
    this.obterContribuicao();
    this.obterEvento(state.codAudienciaPublica);
  }

  async executarFluxoSalvamento({
    ocultarDialogSalvar,
    onEtapaChange,
    finalizar,
  }: ExecutarFluxoSalvamentoParams) {
    const temAnexo = this.arquivoList.length > 0;
    let etapaAtual: SalvamentoEtapaKey = "salvando";

    try {
      onEtapaChange("salvando");
      const result = await this.criarContribuicao(ocultarDialogSalvar);

      if (temAnexo) {
        etapaAtual = "anexos";
        onEtapaChange("anexos");
        await this.postSave(result.codContribuicao, {
          skipBlockUI: true,
          skipToast: true,
        });
      }

      etapaAtual = "finalizando";
      onEtapaChange("finalizando");
      await finalizar();

      return result;
    } catch (err) {
      onEtapaChange(null);
      if (etapaAtual === "salvando") {
        ToastService.showError("Não foi possível salvar a contribuição.");
      } else if (etapaAtual === "anexos") {
        ToastService.showError(
          "Contribuição salva, mas o envio dos anexos falhou."
        );
      } else {
        ToastService.showError("Não foi possível finalizar a contribuição.");
      }
      throw err;
    }
  }

  async executarFluxoEdicao({ onEtapaChange }: ExecutarFluxoEdicaoParams) {
    const state = this.location.state;
    const codContribuicao = state.codContribuicao;
    const temAnexoNovo = this.arquivoList.length > 0;
    const temExclusao = this.anexosExcluir.length > 0;
    let etapaAtual: SalvamentoEtapaKey = "salvando";

    try {
      let payload: any;
      if (this.sigiloso) {
        payload = {
          codContribuicao,
          dsContribuicao: Utils.removerTrechoSigiloso(
            this.contribuicao.dsContribuicaoSigilosa
          ),
          dsJustificativa: this.contribuicao.dsJustificativa,
          dsContribuicaoSigilosa: this.contribuicao.dsContribuicaoSigilosa,
        };
      } else {
        payload = {
          codContribuicao,
          dsContribuicao: this.contribuicao.dsContribuicao,
          dsJustificativa: this.contribuicao.dsJustificativa,
        };
      }

      onEtapaChange("salvando");
      await this.service.atualizar(payload);

      if (temExclusao || temAnexoNovo) {
        etapaAtual = "anexos";
        onEtapaChange("anexos");

        const noServidor =
          (Array.isArray(this.lista) ? this.lista.length : 0) +
          this.anexosExcluir.length;
        const uploadAntesDaExclusao =
          temAnexoNovo &&
          noServidor + this.arquivoList.length <= LIMITE_ANEXOS;

        if (uploadAntesDaExclusao) {
          await this.enviarAnexosNovos(codContribuicao);
        }

        for (const codigoAnexo of this.anexosExcluir) {
          await this.fileService.deleteAnexoContribuicao(codigoAnexo);
        }

        if (temAnexoNovo && !uploadAntesDaExclusao) {
          await this.enviarAnexosNovos(codContribuicao);
        }

        this.setAnexosExcluir([]);
      }

      etapaAtual = "finalizando";
      onEtapaChange("finalizando");

      return { protocolo: this.contribuicao.protocolo };
    } catch (err) {
      onEtapaChange(null);
      if (etapaAtual === "anexos") {
        await this.recarregarAnexosEdicao();
      }
      if (etapaAtual === "salvando") {
        ToastService.showError("Não foi possível atualizar a contribuição.");
      } else if (etapaAtual === "anexos") {
        ToastService.showError(
          "Contribuição atualizada, mas houve falha ao atualizar os anexos."
        );
      } else {
        ToastService.showError("Não foi possível concluir a alteração.");
      }
      throw err;
    }
  }

  private async enviarAnexosNovos(codContribuicao: any) {
    await this.postSave(codContribuicao, {
      skipBlockUI: true,
      skipToast: true,
    });
    this.setArquivoList([]);
  }

  private async recarregarAnexosEdicao() {
    const state = this.location.state;
    try {
      const res: any = await this.service.getAnexoContribuicao(
        this.getCodContribuinte(),
        state.codContribuicao
      );
      const anexos = Array.isArray(res?.result) ? res.result : [];
      this.setLista(anexos);
      this.setAnexosExcluir([]);
    } catch {
      return;
    }
  }

  private async criarContribuicao(ocultarDialogSalvar: boolean) {
    const membro = JSON.parse(sessionStorage.getItem("membro") || "{}");
    let obj;
    const payload = {
      tpInscricao: 2,
      nuInscricao: membro.cpf,
      stNotificarNovaAudiencia: true,
      noRazaoSocial: membro.nome,
      tpContribuinte: 2,
      stAtivo: true,
      dsEmail: membro.email,
      cdTipoInteressado: 1,
      cdUf: membro.codUf,
      cdMunicipio: membro.codMunicipio,
      rlContribuinteAreaInteresse: [
        { cdAreaInteresse: 6 },
        { cdAreaInteresse: 2 },
        { cdAreaInteresse: 7 },
        { cdAreaInteresse: 1 },
        { cdAreaInteresse: 5 },
        { cdAreaInteresse: 4 },
        { cdAreaInteresse: 3 },
      ],
    };

    let membroCodContribuinte = null;
    if (Object.keys(membro).length > 0) {
      const isMembro: any = await this.contribuinteService.verificarMembro(
        membro.cpf
      );

      if (isMembro.success) {
        const newPayload = {
          ...payload,
          codContribuinte: isMembro.result.codContribuinte,
        };
        const updated: any = await this.contribuinteService.updateMembro(
          newPayload
        );
        membroCodContribuinte = updated.result.codContribuinte;
        membro.codContribuinte = updated.result.codContribuinte;
        sessionStorage.setItem("membro", JSON.stringify(membro));
      } else {
        const created: any = await this.contribuinteService.create(payload);
        membroCodContribuinte = created.result.codContribuinte;
        membro.codContribuinte = created.result.codContribuinte;
        sessionStorage.setItem("membro", JSON.stringify(membro));
      }
    }

    if (this.sigiloso) {
      obj = {
        codContribuinte:
          membro && membroCodContribuinte
            ? membroCodContribuinte
            : this.getCodContribuinte(),
        codDispositivo: this.getId(),
        dsContribuicao: Utils.removerTrechoSigiloso(
          this.contribuicao.dsContribuicaoSigilosa
        ),
        dsJustificativa: this.contribuicao.dsJustificativa,
        dsContribuicaoSigilosa: this.contribuicao.dsContribuicaoSigilosa,
        stOcultarDialogoContribuicao: ocultarDialogSalvar,
      };
    } else {
      obj = {
        codContribuinte:
          membro && membroCodContribuinte
            ? membroCodContribuinte
            : this.getCodContribuinte(),
        codDispositivo: this.getId(),
        dsContribuicao: this.contribuicao.dsContribuicao,
        dsJustificativa: this.contribuicao.dsJustificativa,
        stOcultarDialogoContribuicao: ocultarDialogSalvar,
      };
    }

    const res: any = await this.service.create(obj);
    return res.result;
  }

  proximaContribuicao() {
    FilaContribuicaoUtil.goToNext(
      this.history,
      this.getId(),
      this.codAudienciaPublica
    );
  }

  postSave(codContribuicao: any, options: PostSaveOptions = {}) {
    const { skipBlockUI = false, skipToast = false } = options;
    if (!skipBlockUI) {
      this.start();
    }
    return this.fileService
      .uploadContribuicao(codContribuicao, this.arquivoList)
      .then((res) => {
        if (!skipToast) {
          ToastService.showSuccess(Mensagem.SUCESSO);
        }
      })
      .catch((err) => {
        console.log(err);
        throw err;
      })
      .finally(() => {
        if (!skipBlockUI) {
          this.stop();
        }
      });
  }

  handleUpload(e: any) {
    const persistidos =
      this.isEdit && Array.isArray(this.lista) ? this.lista.length : 0;
    if (persistidos + this.arquivoList.length >= LIMITE_ANEXOS) {
      ToastService.showWarn(Mensagem.MSG_E052);
      return;
    }
    this.setArquivoList([...this.arquivoList, e.file]);
  }

  removerAnexoSalvo(item: any) {
    ConfirmationUtil.confirm(
      () => {
        this.setLista(
          this.lista.filter(
            (x: any) =>
              x.cdSeqAnexoContribuicao !== item.cdSeqAnexoContribuicao
          )
        );
        this.setAnexosExcluir([
          ...this.anexosExcluir,
          item.cdSeqAnexoContribuicao,
        ]);
      },
      () => true,
      "Deseja remover este anexo?"
    );
  }

  remove(item: any) {
    ConfirmationUtil.confirm(
      () => {
        const indexOf = this.arquivoList.indexOf(item);
        const temp = [...this.arquivoList];
        temp.splice(indexOf, 1);
        this.setArquivoList([...temp]);
      },
      () => true,
      "Deseja remover este arquivo?"
    );
  }

  obterDispositivo() {
    this.start();
    this.eventoService
      .getDispositivo(this.getId(), this.getCodContribuinte() + 0)
      .then((res: any) => {
        this.stop();
        this.dispostivo.sync(res.result);
      })
      .catch(() => this.stop());
  }

  obterContribuicao() {
    const state = this.location.state;
    if (state.isView || state.isEdit) {
      this.start();
      this.service
        .getContribuicao(state.codContribuicao, this.getCodContribuinte())
        .then((res: any) => {
          this.stop();
          this.contribuicao.sync(res.result);
          if (!!res.result.dsContribuicaoSigilosa) {
            this.setSigiloso(true);
          }
        })
        .catch((err) => this.stop());

      if (state.isEdit) {
        this.setAnexosExcluir([]);
        this.obterAnexoContribuicao();
      }
    }
  }

  async obterEvento(idEvento: any) {
    try {
      if (idEvento) {
        const res: any = await this.eventoService.getEvento(
          idEvento,
          this.getCodContribuinte() + 0
        );
        this.setEvento(res.result);
        return res.result;
      }
    } catch (e) {
      return null;
    }
  }

  changeArquivoSigiloso(m: any, index: any, sigiloso: any) {
    const arquivoList = [...this.arquivoList];
    arquivoList[index].sigiloso = !sigiloso;
    this.setArquivoList(arquivoList);
  }

  changeSigiloso() {
    if (this.sigiloso) {
      const arquivoList = [...this.arquivoList];
      arquivoList.forEach((f) => (f.sigiloso = false));
      this.setArquivoList(arquivoList);
      this.contribuicao.setDsContribuicaoSigilosa(
        this.contribuicao.dsContribuicaoSigilosa
      );
      this.contribuicao.setDsContribuicao(
        this.contribuicao.dsContribuicaoSigilosa
          .replace("<p>", "")
          .replace("</p>", "")
          .replace("color: rgb(0, 0, 0);", "")
          .replace("background-color: rgb(0, 0, 0);", "")
          .replace("background-color: rgb(255, 255, 255);", "")
          .replace('<span style=" ">', "")
          .replace("</span>", "")
          .replace('<span style="">', "")
          .replace('<span style="background-">', "")
      );
    } else {
      this.contribuicao.setDsContribuicao(this.contribuicao.dsContribuicao);
      this.contribuicao.setDsContribuicaoSigilosa(
        this.contribuicao.dsContribuicao
      );
    }
    this.setSigiloso(!this.sigiloso);
  }

  downloadContribuicao(e: any) {
    this.start();
    this.fileService
      .downloadContribuicao(e)
      .then((res: any) => {
        this.stop();
        ToastService.showSuccess(Mensagem.DOWNLOAD_SUCESSO);
        this.downloadFile(
          res.data.fileBytes,
          res.data.nome,
          res.data.contentType
        );
      })
      .catch((err) => {
        this.stop();
      });
  }

  obterAnexoContribuicao() {
    const state = this.location.state;
    const contribuinte = this.getCodContribuinte();
    this.start();
    this.service
      .getAnexoContribuicao(contribuinte, state.codContribuicao)
      .then((res: any) => {
        this.stop();
        if (!!res.result && res.result.length > 0) {
          this.setLista(res.result);
        }
      })
      .catch((err) => this.stop());
  }

  async obterAnexoContribuicaoByCodContribuicao(codContribuicao: any) {
    this.start();
    try {
      const res = await this.service.getAnexoContribuicao(
        this.getCodContribuinte(),
        codContribuicao
      );
      this.stop();
      if (!!res.data.result && res.data.result.length > 0) {
        return res.data.result;
      }
      return null;
    } catch (err) {
      this.stop();
      throw err;
    }
  }
}
